import {
  Component,
  ElementRef,
  EventEmitter,
  OnDestroy,
  Output,
  ViewChild,
} from '@angular/core';
import { AiChatbotInputMessageComponent, ChatMessageSubmit } from './ai-chatbot-input-message/ai-chatbot-input-message.component';
import { CommonModule } from '@angular/common';
import { AiApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/ai/ai-api';
import { AuthApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/auth/auth.api';
import { FileApi } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/sdk/file/file-api';
import {
  UploadFileRequest
} from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@dto/requests/dms/upload-file-request';
import { FileType } from '@TheGreenHouseCompany/vegiwise-sdk-typescript/dist/generated/@enums/file-type.enum';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { SharedModule } from '../../../ui-components/shared.module';
import { AiChatbotHeaderComponent } from './ai-chatbot-header/ai-chatbot-header.component';
import { firstValueFrom, Subscription } from 'rxjs';
import { StorageService } from '../../../@services/front/storage.service';
import { PosthogService } from '../../../@services/front/posthog.service';
import { WillyChatService } from '../../../@services/front/willy-chat.service';

/** One bubble of the visible thread. Error and welcome bubbles are kept out
 * of the history sent to the backend. */
interface ChatBubble {
  role: 'user' | 'assistant';
  text: string;
  photoUrls?: string[];
  externalFileUids?: string[];
  excludeFromHistory?: boolean;
}

@Component({
    selector: 'vw-ai-chatbot',
    imports: [
        AiChatbotInputMessageComponent,
        CommonModule,
        TranslateModule,
        SharedModule,
        AiChatbotHeaderComponent,
    ],
    providers: [
        {
            provide: AiApi,
            useClass: AiApi,
        },
        {
            provide: AuthApi,
            useClass: AuthApi,
        },
        {
            provide: FileApi,
            useClass: FileApi,
        },
    ],
    templateUrl: './ai-chatbot.component.html',
    styleUrl: './ai-chatbot.component.scss'
})
export class AiChatbotComponent implements OnDestroy {
  // Emiting input focus so we can display/hide the menu ai actions to let space for the mobile keyboard
  @Output() inputFocus = new EventEmitter<void>();
  @Output() inputBlur = new EventEmitter<void>();

  @ViewChild(AiChatbotInputMessageComponent) inputMessage: AiChatbotInputMessageComponent;
  @ViewChild('thread') thread: ElementRef<HTMLDivElement>;

  inputIsFocus = false;
  chatSessionId: string | undefined;
  messages: ChatBubble[] = [];
  willyLoading = false;
  isGuest = false;

  private openChatSubscription: Subscription;

  constructor(
    private aiApi: AiApi,
    private authApi: AuthApi,
    private fileApi: FileApi,
    private translate: TranslateService,
    private posthogService: PosthogService,
    private willyChatService: WillyChatService,
  ) { }

  async ngOnInit() {
    await this.pushWelcomeMessage();

    // Check if user is authenticated without fetching user data
    const token = this.authApi.decodeToken();
    this.isGuest = token['cognito:groups'].includes('GUEST');

    // Pre-fill the input when the chat is opened from elsewhere (e.g. plant details)
    this.openChatSubscription = this.willyChatService.openChat$.subscribe((prefillText) => {
      if (prefillText) {
        this.inputMessage?.setMessage(prefillText);
      }
    });
  }

  ngOnDestroy() {
    this.openChatSubscription?.unsubscribe();
    this.revokePhotoUrls();
  }

  // Send the message (and optional photos) to the AI and append the reply to the thread
  async sendMessageToWilly(submit: ChatMessageSubmit) {
    if (this.willyLoading) {
      return;
    }
    this.posthogService.capture('willy_message_sent');

    // History BEFORE this turn — the new question travels as `text`
    const history = this.messages
      .filter((m) => !m.excludeFromHistory && m.text)
      .map((m) => ({ role: m.role, text: m.text, externalFileUids: m.externalFileUids }))
      .slice(-20);

    const userBubble: ChatBubble = {
      role: 'user',
      text: submit.text,
      photoUrls: submit.photos?.map((photo) => URL.createObjectURL(photo)),
    };
    this.messages.push(userBubble);
    this.willyLoading = true;
    this.scrollToBottom();

    try {
      let externalFileUids: string[] | undefined;
      let text = submit.text;
      if (submit.photos?.length) {
        this.posthogService.capture('willy_message_with_photo', { count: submit.photos.length });
        try {
          externalFileUids = await Promise.all(submit.photos.map((photo) => this.uploadPhoto(photo)));
          userBubble.externalFileUids = externalFileUids;
        } catch {
          // S3 upload failure is not a Willy failure: dedicated message
          userBubble.excludeFromHistory = true;
          await this.pushErrorMessage('willy.photo.error.upload');
          return;
        }
        if (!text || text.length === 0) {
          text = await firstValueFrom(this.translate.get('willy.photo.default-question'));
          userBubble.text = text;
        }
      }
      const willyResponse = await this.aiApi.chatWithWilly({
        text,
        sessionId: this.chatSessionId,
        externalFileUids,
        history,
      });
      // The vision branch has no Bedrock session: keep the current one alive
      this.chatSessionId = willyResponse.sessionId ?? this.chatSessionId;
      this.messages.push({ role: 'assistant', text: willyResponse.text });
    } catch (error: any) {
      // A failed turn must not pollute the history (a stale user turn would
      // make the backend skip the personalized context for ever after)
      userBubble.excludeFromHistory = true;
      let errorKey = 'willy.error.500';
      switch (error.data?.statusCode) {
        case 404:
          errorKey = 'willy.error.400';
          break;
        case 403:
          errorKey = 'willy.error.403';
          break;
        case 429:
          errorKey = 'willy.error.429';
          break;
      }
      await this.pushErrorMessage(errorKey);
    } finally {
      this.willyLoading = false;
      this.scrollToBottom();
    }
  }

  private async uploadPhoto(photo: File): Promise<string> {
    const uploadFileRequest: UploadFileRequest = new UploadFileRequest();
    uploadFileRequest.file = photo;
    uploadFileRequest.fileName = photo.name;
    uploadFileRequest.fileType = photo.type as FileType;
    const uploaded = await this.fileApi.uploadFile(uploadFileRequest);
    return uploaded.externalFileUid;
  }

  // "New conversation": forget the session and start from a blank thread
  async onNewConversation() {
    if (this.willyLoading) {
      return;
    }
    this.posthogService.capture('willy_conversation_reset');
    this.chatSessionId = undefined;
    this.revokePhotoUrls();
    this.messages = [];
    await this.pushWelcomeMessage();
    this.inputMessage?.resetMessage();
  }

  private async pushWelcomeMessage() {
    this.messages.push({
      role: 'assistant',
      text: await firstValueFrom(this.translate.get('willy.hi')),
      excludeFromHistory: true,
    });
  }

  private async pushErrorMessage(errorKey: string) {
    this.messages.push({
      role: 'assistant',
      text: await firstValueFrom(this.translate.get(errorKey)),
      excludeFromHistory: true,
    });
  }

  private revokePhotoUrls() {
    this.messages.forEach((m) => m.photoUrls?.forEach((url) => URL.revokeObjectURL(url)));
  }

  private scrollToBottom() {
    setTimeout(() => {
      this.thread?.nativeElement?.scroll({
        top: this.thread.nativeElement.scrollHeight,
        behavior: 'smooth',
      });
    }, 50);
  }

  // Emiting input focus so we can display/hide the menu ai actions to let space for the mobile keyboard
  onInputFocus() {
    this.inputIsFocus = true;
    this.inputFocus.emit();
  }

  // Emiting input blur so we can display/hide the menu ai actions to let space for the mobile keyboard
  onInputBlur() {
    this.inputIsFocus = false;
    this.inputBlur.emit();
  }
}
